import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  useAddReplacedPart,
  useAddReplacedPartPhoto,
  useDeleteReplacedPartPhoto,
  useDeviceReplacedParts,
  useDevices,
  useModules,
  useRemoveReplacedPart,
  useReplacedParts,
  useScanStock,
  useStockFieldContext,
  useUpdateReplacedPartSerials
} from '../api/queries'
import type { Project, ReplacedPart, ReplacedPartPhoto, ReplacedPartPhotoKind, StockItem } from '../api/types'
import { prepareDocumentFile } from '../lib/documentPhoto'
import { formatDateTime } from '../lib/format'
import { useAuthedBlob } from '../lib/photos'
import { CodeScanner } from './CodeScanner'
import { Card } from './ui'

/**
 * Udskiftede dele på servicesagen (docs/DECISIONS.md beslutning 098).
 *
 * <b>Montøren skriver selv</b> - den, der skifter batteriet, er den eneste, der
 * ved, at det blev skiftet. Stregkoden på den nye del scannes: står varen i
 * lageret, trækkes den fra bilen som "Brugt på sag", ellers skrives den bare på
 * med navn og stregkode.
 */
export function ReplacedPartsCard({ project }: { project: Project }) {
  const { data: modules } = useModules()
  const enabled = modules?.some((m) => m.key === 'service' && m.enabled) ?? false
  const { data: parts } = useReplacedParts(project.id, enabled)
  const [adding, setAdding] = useState(false)

  if (!enabled) return null

  const canAdd = !project.isArchived

  return (
    <Card
      title={`Udskiftede dele${parts?.length ? ` (${parts.length})` : ''}`}
      actions={
        canAdd && !adding ? (
          <button className="btn btn--sm btn--primary" onClick={() => setAdding(true)}>
            Tilføj udskiftet del
          </button>
        ) : undefined
      }
    >
      {adding && <AddPartForm project={project} onClose={() => setAdding(false)} />}

      {!adding && (!parts || parts.length === 0) && (
        <div className="small muted">
          Intet skrevet endnu. Har du skiftet noget - batterier, en sikring, en lampe - så tryk "Tilføj udskiftet del"
          og scan stregkoden på den nye del.
        </div>
      )}

      {parts && parts.length > 0 && (
        <div className="stack" style={{ marginTop: adding ? 16 : 0 }}>
          {parts.map((part) => (
            <PartRow key={part.id} part={part} showDevice />
          ))}
        </div>
      )}
    </Card>
  )
}

/**
 * Enhedens udskiftede dele på tværs af sager - "hvornår blev batterierne sidst
 * skiftet". Skjult, når der ikke er skiftet noget.
 */
export function DeviceReplacedPartsCard({ deviceId }: { deviceId: number }) {
  const { data: modules } = useModules()
  const enabled = modules?.some((m) => m.key === 'service' && m.enabled) ?? false
  const { data: parts } = useDeviceReplacedParts(deviceId, enabled)

  if (!enabled || !parts || parts.length === 0) return null

  return (
    <Card title={`Udskiftede dele (${parts.length})`}>
      <div className="stack">
        {parts.map((part) => (
          <PartRow key={part.id} part={part} showProject />
        ))}
      </div>
    </Card>
  )
}

/** Teksten ved hver slags billede (beslutning 112). */
const PHOTO_KIND_TEXT: Record<ReplacedPartPhotoKind, string> = {
  approval: 'Godkendelse',
  after: 'Efter udskiftning',
  photo: 'Billede'
}

/**
 * Én udskiftet del. Kan fjernes af den, der skrev den, og af projektledere.
 *
 * Godkendelsen (et skærmbillede) og billedet efter udskiftningen lægges i enhedens mappe i SharePoint
 * (beslutning 112). Serienumrene kan skrives på bagefter - det nye står ofte først på delen, når den er pakket ud.
 */
function PartRow({
  part,
  showDevice,
  showProject
}: {
  part: ReplacedPart
  showDevice?: boolean
  showProject?: boolean
}) {
  const remove = useRemoveReplacedPart(part.projectId)
  const addPhoto = useAddReplacedPartPhoto(part.projectId)
  const [pendingKind, setPendingKind] = useState<ReplacedPartPhotoKind | null>(null)
  const [editingSerials, setEditingSerials] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const canEdit = part.canDelete && !showProject

  const quantity = part.quantity.toLocaleString('da-DK', {
    maximumFractionDigits: 3
  })

  async function removePart() {
    const back = part.fromLocationName ? ` Den lægges tilbage i "${part.fromLocationName}".` : ''
    if (!window.confirm(`Fjern "${part.itemName ?? part.description}" fra sagen?${back}`)) return

    setError(null)
    try {
      await remove.mutateAsync(part.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delen kunne ikke fjernes.')
    }
  }

  async function pick(file: File | undefined, kind: ReplacedPartPhotoKind) {
    if (!file) return
    setError(null)
    setPendingKind(kind)
    try {
      const prepared = await prepareDocumentFile(file)
      await addPhoto.mutateAsync({
        id: part.id,
        file: prepared.blob,
        fileName: prepared.fileName,
        kind
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Billedet kunne ikke gemmes.')
    } finally {
      setPendingKind(null)
    }
  }

  const hasApproval = part.photos.some((p) => p.kind === 'approval')
  const hasAfter = part.photos.some((p) => p.kind === 'after')

  return (
    <div style={{ borderLeft: '3px solid #0EA5E9', paddingLeft: 10 }}>
      <div className="row row--between" style={{ gap: 8, alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div className="small">
            <strong>
              {quantity} {part.unitName}
            </strong>
            {part.itemName && <> · {part.itemName}</>}
          </div>
          <div className="small">{part.description}</div>
          <div className="tiny muted">
            {formatDateTime(part.createdAt)}
            {part.createdBy && <> · {part.createdBy.displayName}</>}
            {showDevice && part.deviceId && (
              <>
                {' · '}
                <Link to={`/devices/${part.deviceId}`}>{part.deviceName}</Link>
              </>
            )}
            {showProject && (
              <>
                {' · '}
                <Link to={`/projects/${part.projectId}`}>{part.projectName}</Link>
              </>
            )}
            {part.fromLocationName && <> · fra {part.fromLocationName}</>}
            {part.barcode && <> · stregkode {part.barcode}</>}
          </div>
          {(part.oldSerialNumber || part.newSerialNumber) && !editingSerials && (
            <div className="tiny" style={{ marginTop: 2 }}>
              Serienr.: <span className="muted">gammel</span> {part.oldSerialNumber ?? '-'}{' '}
              <span className="muted">→ ny</span> {part.newSerialNumber ?? '-'}
            </div>
          )}
        </div>

        {canEdit && (
          <button
            className="btn btn--sm"
            style={{ flexShrink: 0 }}
            disabled={remove.isPending}
            onClick={() => void removePart()}
          >
            Fjern
          </button>
        )}
      </div>

      {canEdit && !editingSerials && (
        <div className="row" style={{ gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          {/* Godkendelsen er typisk et skærmbillede fra telefonen - derfor intet kamera her. */}
          <PhotoButton
            text={hasApproval ? '+ Godkendelse' : '📄 Godkendelse'}
            busy={pendingKind === 'approval'}
            disabled={addPhoto.isPending}
            onFile={(file) => void pick(file, 'approval')}
          />
          <PhotoButton
            text={hasAfter ? '+ Efter udskiftning' : '📷 Efter udskiftning'}
            busy={pendingKind === 'after'}
            disabled={addPhoto.isPending}
            camera
            onFile={(file) => void pick(file, 'after')}
          />
          <button className="btn btn--sm" type="button" onClick={() => setEditingSerials(true)}>
            {part.oldSerialNumber || part.newSerialNumber ? 'Ret serienumre' : 'Serienumre'}
          </button>
        </div>
      )}

      {editingSerials && <SerialsForm part={part} onClose={() => setEditingSerials(false)} />}

      {part.photos.length > 0 && (
        <div className="row" style={{ gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          {part.photos.map((photo) => (
            <PartPhoto key={photo.id} photo={photo} projectId={part.projectId} partId={part.id} canDelete={canEdit} />
          ))}
        </div>
      )}

      {error && (
        <div className="error-box" style={{ marginTop: 6 }}>
          {error}
        </div>
      )}
    </div>
  )
}

/** En knap, der åbner en filvælger. `camera` foreslår telefonens kamera frem for billedgalleriet. */
function PhotoButton({
  text,
  busy,
  disabled,
  camera,
  onFile
}: {
  text: string
  busy: boolean
  disabled: boolean
  camera?: boolean
  onFile: (file: File | undefined) => void
}) {
  return (
    <label className="btn btn--sm" style={{ cursor: disabled ? 'default' : 'pointer', opacity: disabled && !busy ? 0.6 : 1 }}>
      {busy ? 'Gemmer ...' : text}
      <input
        type="file"
        accept="image/*"
        capture={camera ? 'environment' : undefined}
        disabled={disabled}
        style={{ display: 'none' }}
        onChange={(e) => {
          onFile(e.target.files?.[0])
          e.target.value = ''
        }}
      />
    </label>
  )
}

/** Serienumrene på den udtagne og den nye del. Tomt felt rydder nummeret. */
function SerialsForm({ part, onClose }: { part: ReplacedPart; onClose: () => void }) {
  const update = useUpdateReplacedPartSerials(part.projectId)
  const [oldSerial, setOldSerial] = useState(part.oldSerialNumber ?? '')
  const [newSerial, setNewSerial] = useState(part.newSerialNumber ?? '')
  const [error, setError] = useState<string | null>(null)

  async function save(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      await update.mutateAsync({ id: part.id, oldSerialNumber: oldSerial, newSerialNumber: newSerial })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Serienumrene kunne ikke gemmes.')
    }
  }

  return (
    <form className="stack" style={{ marginTop: 8 }} onSubmit={save}>
      <SerialFields oldSerial={oldSerial} newSerial={newSerial} onOld={setOldSerial} onNew={setNewSerial} autoFocus />
      {error && <div className="error-box">{error}</div>}
      <div className="btn-group">
        <button className="btn btn--sm btn--primary" type="submit" disabled={update.isPending}>
          {update.isPending ? 'Gemmer ...' : 'Gem serienumre'}
        </button>
        <button className="btn btn--sm" type="button" onClick={onClose}>
          Fortryd
        </button>
      </div>
    </form>
  )
}

/** Felterne til serienumrene - de samme i formularen og ved rettelse. */
function SerialFields({
  oldSerial,
  newSerial,
  onOld,
  onNew,
  autoFocus
}: {
  oldSerial: string
  newSerial: string
  onOld: (value: string) => void
  onNew: (value: string) => void
  autoFocus?: boolean
}) {
  return (
    <div className="form-grid">
      <label className="field">
        <span className="field__label">Serienr. på den gamle del</span>
        <input
          autoFocus={autoFocus}
          maxLength={100}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          value={oldSerial}
          onChange={(e) => onOld(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="field__label">Serienr. på den nye del</span>
        <input
          maxLength={100}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          value={newSerial}
          onChange={(e) => onNew(e.target.value)}
        />
      </label>
    </div>
  )
}

/** Ét billede med hvad det viser. Åbner i fuld størrelse ved tryk. */
function PartPhoto({
  photo,
  projectId,
  partId,
  canDelete
}: {
  photo: ReplacedPartPhoto
  projectId: number
  partId: number
  canDelete: boolean
}) {
  const url = useAuthedBlob(photo.url)
  const remove = useDeleteReplacedPartPhoto(projectId)
  const label = PHOTO_KIND_TEXT[photo.kind] ?? 'Billede'

  async function removePhoto() {
    if (!window.confirm(`Fjern billedet "${label}"? Det havner i SharePoints papirkurv.`)) return
    try {
      await remove.mutateAsync({ id: partId, photoId: photo.id })
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Billedet kunne ikke fjernes.')
    }
  }

  return (
    <div style={{ width: 88 }}>
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" title={label}>
          <img src={url} alt={label} style={{ width: 88, height: 88, objectFit: 'cover', borderRadius: 6 }} />
        </a>
      ) : (
        <div style={{ width: 88, height: 88, borderRadius: 6, background: '#F1F5F9' }} />
      )}
      <div className="tiny muted row row--between" style={{ gap: 4 }}>
        <span>{label}</span>
        {canDelete && (
          <button
            type="button"
            className="link-button tiny"
            title="Fjern billedet"
            disabled={remove.isPending}
            onClick={() => void removePhoto()}
          >
            ✕
          </button>
        )}
      </div>
    </div>
  )
}

/** Tomt = ikke fra lageret. */
const NOT_FROM_STOCK = ''

/**
 * Formularen: enhed, scan, antal, hvad der er skiftet og fotos.
 *
 * <b>Scanningen er en hjælp, ikke et krav.</b> En del uden stregkode, eller en med
 * en slidt stregkode, skrives bare på med tekst.
 */
function AddPartForm({ project, onClose }: { project: Project; onClose: () => void }) {
  const { data: modules } = useModules()
  const stockEnabled = modules?.some((m) => m.key === 'stock' && m.enabled) ?? false

  const { data: devices } = useDevices({
    serviceProjectId: project.id,
    includeInactive: true
  })
  const { data: context } = useStockFieldContext()
  const scan = useScanStock()
  const add = useAddReplacedPart(project.id)
  const addPhoto = useAddReplacedPartPhoto(project.id)

  const linked = devices ?? []
  const [deviceId, setDeviceId] = useState<string>('')
  const [item, setItem] = useState<StockItem | null>(null)
  const [barcode, setBarcode] = useState<string | null>(null)
  const [itemName, setItemName] = useState('')
  const [fromLocationId, setFromLocationId] = useState<string | null>(null)
  const [quantity, setQuantity] = useState('1')
  const [description, setDescription] = useState('')
  const [oldSerial, setOldSerial] = useState('')
  const [newSerial, setNewSerial] = useState('')
  // Skærmbilledet af godkendelsen og billederne efter udskiftningen (beslutning 112).
  const [approvalFiles, setApprovalFiles] = useState<File[]>([])
  const [afterFiles, setAfterFiles] = useState<File[]>([])
  const [scanMessage, setScanMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Én enhed paa sagen: saa er der intet at vaelge.
  const chosenDevice = deviceId || (linked.length === 1 ? String(linked[0].id) : '')

  // Bilen, montoeren staar som ansvarlig for, er det normale sted at tage fra.
  // Null = montoeren har ikke valgt, saa den foreslaas.
  const from = fromLocationId ?? (context?.myLocation ? String(context.myLocation.id) : NOT_FROM_STOCK)

  const locations = (context?.locations ?? []).filter((l) => l.kind === 'van' || l.kind === 'warehouse')

  async function onCode(code: string) {
    setScanMessage(null)
    setError(null)

    if (!stockEnabled) {
      setBarcode(code)
      return
    }

    try {
      const result = await scan.mutateAsync(code)
      if (result.kind === 'item' && result.item) {
        setItem(result.item)
        setBarcode(result.item.barcode ?? result.scannedCode)
        setItemName('')
        if (!description.trim()) setDescription(result.item.name)
      } else {
        setItem(null)
        setBarcode(result.scannedCode)
        setScanMessage(
          result.kind === 'barcode_unknown'
            ? 'Stregkoden står ikke i lageret. Skriv hvad delen er - den skrives på sagen uden at røre lageret.'
            : (result.message ?? 'Koden blev ikke genkendt. Skriv hvad delen er.')
        )
      }
    } catch (err) {
      setScanMessage(err instanceof Error ? err.message : 'Koden kunne ikke slås op.')
    }
  }

  function clearItem() {
    setItem(null)
    setBarcode(null)
    setScanMessage(null)
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)

    const amount = Number(quantity.replace(',', '.'))

    let created: ReplacedPart
    try {
      created = await add.mutateAsync({
        deviceId: chosenDevice ? Number(chosenDevice) : null,
        description: description.trim(),
        quantity: amount,
        stockItemId: item?.id ?? null,
        itemName: item ? null : itemName.trim() || null,
        barcode,
        unit: null,
        fromLocationId: item && from ? Number(from) : null,
        oldSerialNumber: oldSerial.trim() || null,
        newSerialNumber: newSerial.trim() || null
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delen kunne ikke gemmes.')
      return
    }

    // Delen foerst, billederne bagefter - de skal haenge paa noget.
    try {
      const uploads: [File, ReplacedPartPhotoKind][] = [
        ...approvalFiles.map((file): [File, ReplacedPartPhotoKind] => [file, 'approval']),
        ...afterFiles.map((file): [File, ReplacedPartPhotoKind] => [file, 'after'])
      ]
      for (const [file, kind] of uploads) {
        const prepared = await prepareDocumentFile(file)
        await addPhoto.mutateAsync({
          id: created.id,
          file: prepared.blob,
          fileName: prepared.fileName,
          kind
        })
      }
    } catch (err) {
      setError(
        `Delen er gemt, men et billede kom ikke med (${err instanceof Error ? err.message : 'ukendt fejl'}). ` +
          'Tryk "Godkendelse" eller "Efter udskiftning" ved delen for at prøve igen.'
      )
      return
    }

    onClose()
  }

  const busy = add.isPending || addPhoto.isPending

  // Scanneren har sin egen <form> (tastefeltet). Formularer kan ikke ligge inden i
  // hinanden - saa ville Enter i tastefeltet gemme delen i stedet for at slaa koden
  // op. Derfor staar enhed og scanner udenfor, og vaelgeren peger ind med form=.
  const formId = `replaced-part-${project.id}`

  return (
    <div className="stack">
      {linked.length > 1 && (
        <label className="field">
          <span className="field__label">Hvilken enhed sidder delen i? *</span>
          <select form={formId} required value={chosenDevice} onChange={(e) => setDeviceId(e.target.value)}>
            <option value="">Vælg enhed</option>
            {linked.map((device) => (
              <option key={device.id} value={device.id}>
                {device.name}
                {device.location ? ` · ${device.location}` : ''}
              </option>
            ))}
          </select>
        </label>
      )}
      {linked.length === 1 && <div className="small muted">Enhed: {linked[0].name}</div>}

      <div className="field">
        <span className="field__label">Scan stregkoden på den nye del</span>
        {item ? (
          <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="small">
              ✓ <strong>{item.name}</strong> <span className="tiny muted">{item.code}</span>
            </span>
            <button type="button" className="btn btn--sm" onClick={clearItem}>
              Scan en anden
            </button>
          </div>
        ) : (
          <CodeScanner
            onCode={(code) => void onCode(code)}
            busy={scan.isPending}
            hint={stockEnabled ? 'Står varen i lageret, trækkes den fra bilen.' : undefined}
          />
        )}
        {barcode && !item && <span className="tiny muted">Stregkode: {barcode}</span>}
        {scanMessage && <span className="tiny warn">{scanMessage}</span>}
      </div>

      <form id={formId} className="stack" onSubmit={submit}>
        {item ? (
          <label className="field">
            <span className="field__label">Taget fra</span>
            <select value={from} onChange={(e) => setFromLocationId(e.target.value)}>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                  {context?.myLocation?.id === location.id ? ' (min bil)' : ''}
                </option>
              ))}
              <option value={NOT_FROM_STOCK}>Ikke fra lageret - købt direkte</option>
            </select>
          </label>
        ) : (
          <label className="field">
            <span className="field__label">Hvad er delen?</span>
            <input
              placeholder="F.eks. Batteri 12V 7Ah"
              maxLength={200}
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
            />
          </label>
        )}

        <label className="field">
          <span className="field__label">Antal{item ? ` (${item.unitName})` : ''} *</span>
          <input
            required
            inputMode="decimal"
            style={{ maxWidth: 120 }}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">Hvad er udskiftet? *</span>
          <textarea
            required
            maxLength={1000}
            placeholder="F.eks. Skiftet 4 batterier, de gamle var svulmede"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <SerialFields oldSerial={oldSerial} newSerial={newSerial} onOld={setOldSerial} onNew={setNewSerial} />

        <label className="field">
          <span className="field__label">
            Skærmbillede af godkendelsen{' '}
            {approvalFiles.length > 0 ? (
              <span className="tiny muted">({approvalFiles.length} valgt)</span>
            ) : (
              <span className="tiny muted">(frivilligt)</span>
            )}
          </span>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => setApprovalFiles(Array.from(e.target.files ?? []))}
          />
        </label>

        <label className="field">
          <span className="field__label">
            Billede efter udskiftning{' '}
            {afterFiles.length > 0 && <span className="tiny muted">({afterFiles.length} valgt)</span>}
          </span>
          <input type="file" accept="image/*" multiple onChange={(e) => setAfterFiles(Array.from(e.target.files ?? []))} />
          <span className="tiny muted">Billederne lægges i enhedens mappe i SharePoint.</span>
        </label>

        {error && <div className="error-box">{error}</div>}

        <div className="btn-group">
          <button className="btn btn--primary" type="submit" disabled={busy}>
            {busy ? 'Gemmer ...' : 'Gem'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Fortryd
          </button>
        </div>
      </form>
    </div>
  )
}
